import { useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Form,
  Input,
  Modal,
  Result,
  Row,
  Skeleton,
  Space,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd'
import {
  CheckCircleOutlined,
  DollarOutlined,
  EditOutlined,
  PrinterOutlined,
  SendOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { errorTag, translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { TWO_COLUMNS } from '@/shared/ui/descriptionColumns'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DISCOUNT_STATE_COLOR } from '@/features/cashdesk/salesApi'
import { fetchPaymentCategories, paymentCategoryName } from '@/features/cashdesk/paymentCategoryApi'
import { fetchStockItems, fetchWarehouses } from '@/features/inventory/stockApi'
import {
  INVOICE_STATUS_COLOR,
  INVOICE_TEXT_MAX,
  cancelInvoice,
  fetchInvoice,
  formatInvoiceDay,
  issueInvoice,
  requestInvoiceDiscount,
  type InvoiceDetail,
  type InvoiceLine,
} from '@/features/invoice/invoiceApi'
import { InvoiceFormModal } from '@/features/invoice/InvoiceFormModal'
import { InvoicePaymentModal } from '@/features/invoice/InvoicePaymentModal'
import { useInvoicePrinter } from '@/features/invoice/useInvoicePrinter'

export function InvoiceDetailPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams<{ id: string }>()
  const { can, canAll } = usePermissions()
  const queryClient = useQueryClient()
  const printer = useInvoicePrinter()
  const [editing, setEditing] = useState(false)
  const [issuing, setIssuing] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [paying, setPaying] = useState<InvoiceDetail | null>(null)

  const query = useQuery({
    queryKey: queryKeys.invoices.detail(id),
    queryFn: ({ signal }) => fetchInvoice(id, signal),
    enabled: id !== '',
  })
  const sellsStock = (query.data?.lines ?? []).some((line) => line.itemId)
  const categories = useQuery({
    queryKey: queryKeys.paymentCategories,
    queryFn: ({ signal }) => fetchPaymentCategories(signal),
    staleTime: 5 * 60_000,
  })
  const stockItems = useQuery({
    queryKey: queryKeys.stock.items(false),
    queryFn: () => fetchStockItems(false),
    enabled: sellsStock && can('inventory.stock.read'),
  })
  const warehouses = useQuery({
    queryKey: queryKeys.stock.warehouses,
    queryFn: fetchWarehouses,
    enabled: sellsStock && can('inventory.stock.read'),
  })

  const refresh = (invoice: InvoiceDetail) => {
    queryClient.setQueryData(queryKeys.invoices.detail(invoice.id), invoice)
    void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.list })
  }

  const askDiscount = useMutation({
    mutationFn: () => requestInvoiceDiscount(id),
    onSuccess: (invoice) => {
      refresh(invoice)
      void queryClient.invalidateQueries({ queryKey: queryKeys.cashdesk.discounts })
    },
  })

  if (query.isPending) return <Skeleton active />
  if (query.isError) {
    return <Result status="warning" title={t('invoices.notFound')} subTitle={translateError(query.error, t)} />
  }

  const invoice = query.data
  const draft = invoice.state === 'DRAFT'
  const request = invoice.discountRequest
  const lineDetails = (line: InvoiceLine) => {
    const category = (categories.data ?? []).find((c) => c.id === line.categoryId)
    const item = (stockItems.data ?? []).find((candidate) => candidate.id === line.itemId)
    const warehouse = (warehouses.data ?? []).find((candidate) => candidate.id === line.warehouseId)
    return [
      t(`invoices.lineKind.${line.kind}`),
      category ? paymentCategoryName(category, t) : null,
      item?.name,
      warehouse?.displayName,
    ]
      .filter(Boolean)
      .join(' · ')
  }
  const hasReceiptPayments = invoice.allocations
    .filter((a) => a.kind !== 'ISSUE_BALANCE')
    .reduce((sum, a) => sum + a.amount.amount, 0) !== 0

  return (
    <>
      <PageHeader
        title={t('invoices.detailTitle', { no: invoice.invoiceNo ?? t('invoices.draftNumber') })}
        description={t('pageIntro.invoiceDetail')}
        extra={
          <Space wrap>
            <Tag color={INVOICE_STATUS_COLOR[invoice.status]}>{t(`invoices.status.${invoice.status}`)}</Tag>
            {draft && can('invoice.create') ? (
              <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
                {t('common.edit')}
              </Button>
            ) : null}
            {draft && can('invoice.create') ? (
              <Button type="primary" icon={<SendOutlined />} onClick={() => setIssuing(true)}>
                {t('invoices.issue')}
              </Button>
            ) : null}
            {invoice.state === 'ISSUED' && invoice.outstanding.amount > 0 && can('cash.receipt.create') ? (
              <Button type="primary" icon={<DollarOutlined />} onClick={() => setPaying(invoice)}>
                {t('invoices.takePayment')}
              </Button>
            ) : null}
            {invoice.state !== 'DRAFT' ? (
              <Button icon={<PrinterOutlined />} loading={printer.printing} onClick={() => void printer.print(invoice)}>
                {invoice.printCount > 0 ? t('invoices.reprint') : t('invoices.print.action')}
              </Button>
            ) : null}
            {invoice.state !== 'CANCELLED' && can('invoice.cancel') ? (
              <Tooltip title={hasReceiptPayments ? t('common.errorTag.INVOICE_HAS_PAYMENTS') : undefined}>
                <Button danger icon={<StopOutlined />} disabled={hasReceiptPayments} onClick={() => setCancelling(true)}>
                  {t('invoices.cancel')}
                </Button>
              </Tooltip>
            ) : null}
          </Space>
        }
      />

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {printer.error ? <Alert type="error" showIcon message={translateError(printer.error, t)} /> : null}
        {invoice.state === 'CANCELLED' ? (
          <Alert
            type="warning"
            showIcon
            message={t('invoices.cancelledBy', {
              name: invoice.cancelledBy?.name ?? t('common.dash'),
              at: formatDateTime(invoice.cancelledAt),
            })}
            description={invoice.cancelReason ?? undefined}
          />
        ) : null}

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <Card title={t('invoices.customerTitle')}>
              <Descriptions size="small" column={TWO_COLUMNS} bordered>
                <Descriptions.Item label={t('cashdesk.receipt.login')}>
                  <Link to={`/subscribers/${invoice.customer.subscriberId}?tab=invoices`}>{invoice.customer.login}</Link>
                </Descriptions.Item>
                <Descriptions.Item label={t('cashdesk.receipt.customerId')}>
                  {invoice.customer.customerId ?? t('common.dash')}
                </Descriptions.Item>
                <Descriptions.Item label={t('cashdesk.receipt.fullName')}>
                  {invoice.customer.name ?? t('common.dash')}
                </Descriptions.Item>
                <Descriptions.Item label={t('cashdesk.receipt.phone')}>
                  {invoice.customer.phone ?? t('common.dash')}
                </Descriptions.Item>
                <Descriptions.Item label={t('invoices.print.address')}>
                  {invoice.customer.address ?? t('common.dash')}
                </Descriptions.Item>
                <Descriptions.Item label={t('invoices.print.branch')}>{invoice.branch.name}</Descriptions.Item>
                <Descriptions.Item label={t('invoices.print.center')}>
                  {invoice.customer.center ?? t('common.dash')}
                </Descriptions.Item>
                <Descriptions.Item label={t('invoices.print.visp')}>
                  {invoice.customer.visp ?? t('common.dash')}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card title={t('invoices.totalsTitle')}>
              <Descriptions size="small" column={1} bordered>
                <Descriptions.Item label={t('invoices.print.date')}>{formatInvoiceDay(invoice.invoiceDate)}</Descriptions.Item>
                <Descriptions.Item label={t('invoices.print.due')}>{formatInvoiceDay(invoice.dueDate)}</Descriptions.Item>
                <Descriptions.Item label={t('invoices.subtotal')}>{formatMoneyValue(invoice.subtotal)}</Descriptions.Item>
                <Descriptions.Item label={t('invoices.discount')}>{formatMoneyValue(invoice.discount)}</Descriptions.Item>
                <Descriptions.Item label={t('invoices.total')}>
                  <Typography.Text strong>{formatMoneyValue(invoice.total)}</Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('invoices.paid')}>{formatMoneyValue(invoice.paid)}</Descriptions.Item>
                <Descriptions.Item label={t('invoices.outstanding')}>
                  <Typography.Text strong type={invoice.outstanding.amount > 0 ? 'danger' : undefined}>
                    {formatMoneyValue(invoice.outstanding)}
                  </Typography.Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>

        {invoice.discount.amount > 0 ? (
          <Card title={t('invoices.discountTitle')}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {invoice.discountReason ? <Typography.Text>{invoice.discountReason}</Typography.Text> : null}
              {request ? (
                <Space wrap>
                  <Tag color={DISCOUNT_STATE_COLOR[request.state]}>{t(`discounts.state.${request.state}`)}</Tag>
                  {request.decidedBy ? (
                    <Typography.Text type="secondary">
                      {request.decidedBy.name} · {formatDateTime(request.decidedAt)}
                      {request.decisionNote ? ` — ${request.decisionNote}` : ''}
                    </Typography.Text>
                  ) : null}
                </Space>
              ) : invoice.state === 'CANCELLED' ? null : (
                <Typography.Text type="secondary">{t('invoices.discountNoRequest')}</Typography.Text>
              )}
              {draft &&
              canAll('invoice.create', 'cash.discount.apply') &&
              (!request || request.state === 'REJECTED' || request.state === 'CANCELLED') ? (
                <Button
                  icon={<CheckCircleOutlined />}
                  loading={askDiscount.isPending}
                  onClick={() => askDiscount.mutate()}
                >
                  {t('sales.requestApproval')}
                </Button>
              ) : null}
              {askDiscount.isError ? (
                <Alert type="error" showIcon message={translateError(askDiscount.error, t)} />
              ) : null}
            </Space>
          </Card>
        ) : null}

        <Card title={t('invoices.form.lines')}>
          <Table<InvoiceLine>
            size="small"
            pagination={false}
            rowKey={(line) => line.id}
            dataSource={invoice.lines}
            scroll={{ x: 700 }}
            columns={[
              { key: 'no', title: '#', width: 48, render: (_, line) => line.lineNo },
              {
                key: 'description',
                title: t('invoices.print.description'),
                render: (_, line) => (
                  <Space direction="vertical" size={0}>
                    <span>{line.description ?? t(`invoices.lineKind.${line.kind}`)}</span>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {lineDetails(line)}
                    </Typography.Text>
                  </Space>
                ),
              },
              { key: 'qty', title: t('invoices.print.quantity'), width: 90, align: 'right', render: (_, line) => line.quantity },
              {
                key: 'unit',
                title: t('invoices.print.unitPrice'),
                width: 150,
                align: 'right',
                render: (_, line) => formatMoneyValue(line.unitPrice),
              },
              {
                key: 'amount',
                title: t('invoices.print.amount'),
                width: 150,
                align: 'right',
                render: (_, line) => formatMoneyValue(line.amount),
              },
            ]}
          />
          {invoice.notes ? (
            <Typography.Paragraph type="secondary" style={{ marginTop: 12, whiteSpace: 'pre-wrap' }}>
              {invoice.notes}
            </Typography.Paragraph>
          ) : null}
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title={t('invoices.allocationsTitle')}>
              <Table
                size="small"
                pagination={false}
                rowKey={(row) => `${row.kind}-${row.at}-${row.receiptId ?? ''}`}
                dataSource={invoice.allocations}
                locale={{ emptyText: t('invoices.noAllocations') }}
                columns={[
                  { key: 'at', title: t('cashdesk.columns.date'), render: (_, row) => formatDateTime(row.at) },
                  {
                    key: 'kind',
                    title: t('invoices.allocationKind.title'),
                    render: (_, row) => t(`invoices.allocationKind.${row.kind}`),
                  },
                  { key: 'receipt', title: t('cashdesk.columns.receiptNo'), render: (_, row) => row.receiptNo ?? t('common.dash') },
                  {
                    key: 'amount',
                    title: t('cashdesk.columns.amount'),
                    align: 'right',
                    render: (_, row) => formatMoneyValue(row.amount),
                  },
                ]}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title={t('invoices.historyTitle')}>
              <Timeline
                items={invoice.events.map((event) => ({
                  children: (
                    <Space direction="vertical" size={0}>
                      <Typography.Text>{t(`invoices.event.${event.event}`, { defaultValue: event.event })}</Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {[event.actor?.name, formatDateTime(event.at)].filter(Boolean).join(' · ')}
                      </Typography.Text>
                    </Space>
                  ),
                }))}
              />
            </Card>
          </Col>
        </Row>
      </Space>

      <InvoiceFormModal
        open={editing}
        invoice={invoice}
        onClose={() => setEditing(false)}
        onSaved={(saved) => {
          setEditing(false)
          refresh(saved)
        }}
      />
      <IssueModal invoice={issuing ? invoice : null} onClose={() => setIssuing(false)} onDone={refresh} />
      <CancelModal invoice={cancelling ? invoice : null} onClose={() => setCancelling(false)} onDone={refresh} />
      <InvoicePaymentModal invoice={paying} onClose={() => setPaying(null)} />
      {printer.portal}
    </>
  )
}

function IssueModal({
  invoice,
  onClose,
  onDone,
}: {
  invoice: InvoiceDetail | null
  onClose: () => void
  onDone: (invoice: InvoiceDetail) => void
}) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const [allowCredit, setAllowCredit] = useState(false)

  const issue = useMutation({
    mutationFn: () => issueInvoice(invoice?.id ?? '', allowCredit),
    onSuccess: (issued) => {
      onDone(issued)
      setAllowCredit(false)
      onClose()
    },
  })
  const creditAsked = errorTag(issue.error) === 'CASH_CREDIT_CONFIRMATION_REQUIRED'

  return (
    <Modal
      open={invoice !== null}
      title={t('invoices.issueTitle')}
      okText={t('invoices.issue')}
      cancelText={t('common.cancel')}
      confirmLoading={issue.isPending}
      onCancel={() => {
        issue.reset()
        setAllowCredit(false)
        onClose()
      }}
      destroyOnHidden
      onOk={() => issue.mutate()}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="warning" showIcon message={t('invoices.issueWarn')} />
        {issue.isError ? <Alert type="error" showIcon message={translateError(issue.error, t)} /> : null}
        {can('cash.sale.credit') ? (
          <Checkbox checked={allowCredit} onChange={(e) => setAllowCredit(e.target.checked)}>
            {t('invoices.allowCredit')}
          </Checkbox>
        ) : null}
        {creditAsked && !can('cash.sale.credit') ? (
          <Alert type="error" showIcon message={t('sales.creditForbidden')} />
        ) : null}
      </Space>
    </Modal>
  )
}

function CancelModal({
  invoice,
  onClose,
  onDone,
}: {
  invoice: InvoiceDetail | null
  onClose: () => void
  onDone: (invoice: InvoiceDetail) => void
}) {
  const { t } = useTranslation()
  const [form] = Form.useForm<{ reason?: string }>()
  const cancel = useMutation({
    mutationFn: (reason: string) => cancelInvoice(invoice?.id ?? '', reason),
    onSuccess: (cancelled) => {
      onDone(cancelled)
      onClose()
    },
  })

  return (
    <Modal
      open={invoice !== null}
      title={t('invoices.cancelTitle')}
      okText={t('invoices.cancel')}
      okButtonProps={{ danger: true }}
      cancelText={t('common.close')}
      confirmLoading={cancel.isPending}
      onCancel={() => {
        cancel.reset()
        onClose()
      }}
      destroyOnHidden
      onOk={() => submitOnValid(form, (values) => cancel.mutate(values.reason?.trim() ?? ''))}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message={invoice?.state === 'ISSUED' ? t('invoices.cancelIssuedWarn') : t('invoices.cancelDraftWarn')}
        />
        {cancel.isError ? <Alert type="error" showIcon message={translateError(cancel.error, t)} /> : null}
        <Form form={form} layout="vertical" requiredMark preserve={false}>
          <Form.Item
            name="reason"
            label={t('actions.reason')}
            rules={[
              { required: true, whitespace: true, message: t('cashdesk.decision.reasonRequired') },
              { max: INVOICE_TEXT_MAX, message: t('cashdesk.reasonTooLong') },
            ]}
          >
            <Input.TextArea rows={3} maxLength={INVOICE_TEXT_MAX} showCount />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  )
}
